import { motion } from "framer-motion";
import {
  Plane, ShieldCheck, ReceiptText, Wallet, RefreshCcw, Globe2, CreditCard, Link2, BarChart3,
} from "lucide-react";

const MODULES = [
  { icon: Plane, title: "Pre-Travel Booking Requests", desc: "Trip initialization with destination, purpose, dates, and logistics preferences — flights, trains, hotels, local transit.", span: "md:col-span-2" },
  { icon: ShieldCheck, title: "Policy & Tier Matrix Engine", desc: "Grade-based entitlements (L1–L3), class restrictions, and dynamic hotel caps by city tier.", span: "" },
  { icon: ReceiptText, title: "Post-Travel Settlement & OCR", desc: "Receipt capture, policy cross-checks, and settlement tracking.", span: "" },
  { icon: Wallet, title: "Cash Advances & Per Diem", desc: "Pre-trip funding auto-deducted at settlement; flat daily rates computed from dates, destination, and tier.", span: "" },
  { icon: RefreshCcw, title: "Cancellations & Refunds", desc: "Itinerary change workflows, flight refund credit-shell tracking, and unspent advance returns.", span: "" },
  { icon: Globe2, title: "International Travel & Forex", desc: "Visa/passport document vault, forex currency requests, and multi-currency expenses auto-converted by receipt date.", span: "md:col-span-2" },
  { icon: CreditCard, title: "Corporate Card Auto-Sync", desc: "Statement imports reconciled by attaching receipts — bypassing the reimbursement flow entirely.", span: "" },
  { icon: Link2, title: "HRMS & ERP Integrations", desc: "Live hierarchy and grade sync from HR software; settlement export to SAP, Tally, and Oracle.", span: "" },
  { icon: BarChart3, title: "Spend Analytics & Audit", desc: "Department budget dashboards and organization-wide policy violation flagging.", span: "" },
];

const INTEGRATIONS = ["SAP S/4HANA", "Oracle ERP Cloud", "Tally Prime", "Workday HCM", "BambooHR", "Amadeus GDS", "Sabre GDS", "NetSuite"];

export const Modules = () => {
  return (
    <section id="modules" data-testid="modules-section" className="bg-slate-50 py-24 lg:py-32 scroll-mt-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <p className="font-mono text-[11px] tracking-widest text-blue-600 uppercase">Platform modules</p>
          <h2 className="mt-4 font-heading font-extrabold tracking-tight text-slate-900 text-4xl sm:text-5xl">
            Every edge case, engineered in
          </h2>
          <p className="mt-4 text-slate-500 text-base md:text-lg">
            Core travel-and-expense flows plus the advanced machinery enterprises actually need — forex, credit shells, per diem, and ERP posting.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-3 gap-5">
          {MODULES.map((m, i) => (
            <motion.div
              key={m.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
              data-testid={`module-card-${i}`}
              className={`group rounded-xl border border-slate-200 bg-white p-7 hover:border-blue-400 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-600/5 transition-all duration-300 ${m.span}`}
            >
              <span className="w-11 h-11 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                <m.icon className="w-5 h-5" />
              </span>
              <h3 className="mt-5 font-heading font-bold text-lg text-slate-900">{m.title}</h3>
              <p className="mt-2 text-sm text-slate-500 leading-relaxed">{m.desc}</p>
            </motion.div>
          ))}
        </div>

        <div id="integrations" data-testid="integrations-section" className="mt-20 rounded-xl bg-slate-900 noise-bg relative overflow-hidden p-8 lg:p-12 scroll-mt-24">
          <div className="flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
            <div className="max-w-md">
              <p className="font-mono text-[11px] tracking-widest text-blue-400 uppercase">Zero-copy integration layer</p>
              <h3 className="mt-3 font-heading font-bold text-2xl text-white">
                Plugged into the systems you already run
              </h3>
              <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                Reporting hierarchies and grades sync from your HRMS; approved settlements export to your accounting stack for bank transfer — no re-keying, no CSV gymnastics.
              </p>
            </div>
            <div className="flex flex-wrap gap-3 lg:max-w-xl lg:justify-end">
              {INTEGRATIONS.map((name) => (
                <span
                  key={name}
                  data-testid={`integration-chip-${name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="px-4 py-2 rounded-md border border-white/15 bg-white/5 font-mono text-xs text-slate-200 hover:border-blue-500/60 hover:text-white transition-colors duration-200"
                >
                  {name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
